import { HAZARD_CATEGORIES } from "./hazardCategories.generated";

export type HazardCategoryConflict = {
  classLabel: string;
  codes: string[];
  labels: string[];
};

type Row = (typeof HAZARD_CATEGORIES)[number];

const ROWS_BY_CODE = new Map<string, Row>(HAZARD_CATEGORIES.map((row) => [row.code, row]));
const SEED_ORDER = new Map<string, number>(HAZARD_CATEGORIES.map((row, i) => [row.code, i]));

const SKIN = { into: "SKIN_CORROSION", label: "Skin Corrosion / Irritation" };
const EYE = { into: "SERIOUS_EYE_DAMAGE", label: "Serious Eye Damage / Eye Irritation" };

/** GHS splits these into two classes, but a substance meets only one of each pair. */
const MERGED_CLASSES: Readonly<Record<string, { into: string; label: string }>> = {
  SKIN_CORROSION: SKIN,
  SKIN_IRRITATION: SKIN,
  SERIOUS_EYE_DAMAGE: EYE,
  EYE_IRRITATION: EYE,
};

/** Classified per target organ or effect, so their categories co-occur. */
const NON_EXCLUSIVE_CLASSES = new Set([
  "SPECIFIC_TARGET_ORGAN_TOXICITY_SINGLE_EXPOSURE",
  "SPECIFIC_TARGET_ORGAN_TOXICITY_REPEATED_OR_PROLONGED_EXPOSURE",
]);

/** Additive to the class's tier rather than a tier of it. */
const ADDITIVE_CODES = new Set(["REPRODUCTIVE_TOXICITY_EFFECTS_ON_OR_VIA_LACTATION"]);

const FLAMMABLE_GAS_1A = "FLAMMABLE_GASES_CATEGORY_1A";
/** Flammable-gas flags imply Category 1A; flags of one family exclude each other. */
const FLAMMABLE_GAS_FLAG_FAMILY: Readonly<Record<string, string>> = {
  FLAMMABLE_GASES_PYROPHORIC_GAS_CATEGORY_1A: "PYROPHORIC",
  FLAMMABLE_GASES_CHEMICALLY_UNSTABLE_GAS_CATEGORY_1A_A: "UNSTABLE",
  FLAMMABLE_GASES_CHEMICALLY_UNSTABLE_GAS_CATEGORY_1A_B: "UNSTABLE",
};

/** Classes a chemical can't fall into together, per the App. B table notes of 29 CFR 1910.1200. */
const EXCLUDED_CLASS_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ["AEROSOLS", "CHEMICALS_UNDER_PRESSURE"],
  ...["FLAMMABLE_GASES", "GAS_UNDER_PRESSURE", "FLAMMABLE_LIQUIDS", "FLAMMABLE_SOLIDS"].flatMap(
    (root) => [["AEROSOLS", root] as const, ["CHEMICALS_UNDER_PRESSURE", root] as const]
  ),
  ["DESENSITIZED_EXPLOSIVES", "EXPLOSIVES"],
  ["DESENSITIZED_EXPLOSIVES", "FLAMMABLE_LIQUIDS"],
  ["DESENSITIZED_EXPLOSIVES", "FLAMMABLE_SOLIDS"],
  ["SELF_REACTIVE_CHEMICALS", "EXPLOSIVES"],
  ["SELF_REACTIVE_CHEMICALS", "ORGANIC_PEROXIDES"],
  ["SELF_REACTIVE_CHEMICALS", "OXIDIZING_LIQUIDS"],
  ["SELF_REACTIVE_CHEMICALS", "OXIDIZING_SOLIDS"],
  ["SELF_HEATING_CHEMICALS", "PYROPHORIC_LIQUIDS"],
  ["SELF_HEATING_CHEMICALS", "PYROPHORIC_SOLIDS"],
];

const EXCLUDED_CLASSES = new Set(
  EXCLUDED_CLASS_PAIRS.flatMap(([a, b]) => [`${a}|${b}`, `${b}|${a}`])
);

function rootOf(row: Row): Row {
  let current = row;
  while (current.parentCode) {
    const parent = ROWS_BY_CODE.get(current.parentCode);
    if (!parent) break;
    current = parent;
  }
  return current;
}

/** The exclusive group a code belongs to, or null when it excludes nothing. */
function exclusiveClassOf(code: string): { code: string; label: string } | null {
  const row = ROWS_BY_CODE.get(code);
  if (!row || !row.selectable || ADDITIVE_CODES.has(code)) return null;
  const root = rootOf(row);
  if (root.code === code || NON_EXCLUSIVE_CLASSES.has(root.code)) return null;
  const merged = MERGED_CLASSES[root.code];
  return merged
    ? { code: merged.into, label: merged.label }
    : { code: root.code, label: root.label };
}

function classesExclude(a: string, b: string): boolean {
  const rowA = ROWS_BY_CODE.get(a);
  const rowB = ROWS_BY_CODE.get(b);
  if (!rowA?.selectable || !rowB?.selectable) return false;
  return EXCLUDED_CLASSES.has(`${rootOf(rowA).code}|${rootOf(rowB).code}`);
}

export function hazardCategoriesConflict(a: string, b: string): boolean {
  if (a === b) return false;
  if (classesExclude(a, b)) return true;
  const classA = exclusiveClassOf(a);
  const classB = exclusiveClassOf(b);
  if (!classA || !classB || classA.code !== classB.code) return false;
  const flagA = FLAMMABLE_GAS_FLAG_FAMILY[a];
  const flagB = FLAMMABLE_GAS_FLAG_FAMILY[b];
  if (flagA && flagB) return flagA === flagB;
  if (flagA) return b !== FLAMMABLE_GAS_1A;
  if (flagB) return a !== FLAMMABLE_GAS_1A;
  return true;
}

function bySeedOrder(a: string, b: string): number {
  return (SEED_ORDER.get(a) ?? 0) - (SEED_ORDER.get(b) ?? 0);
}

/** The class two conflicting codes share, or both classes when one excludes the other. */
function conflictGroupOf(a: string, b: string): { code: string; label: string } {
  const shared = exclusiveClassOf(a);
  if (shared && shared.code === exclusiveClassOf(b)?.code) return shared;
  const [first, second] = [a, b].sort(bySeedOrder).map((code) => {
    const row = ROWS_BY_CODE.get(code);
    return row ? rootOf(row) : { code, label: code };
  });
  return { code: `${first.code}|${second.code}`, label: `${first.label} / ${second.label}` };
}

/** One entry per class, or pair of excluding classes, holding categories a substance cannot meet together. */
export function findHazardCategoryConflicts(codes: readonly string[]): HazardCategoryConflict[] {
  const unique = [...new Set(codes)].sort(bySeedOrder);
  const byGroup = new Map<string, { label: string; codes: Set<string> }>();
  unique.forEach((a, index) => {
    for (const b of unique.slice(index + 1)) {
      if (!hazardCategoriesConflict(a, b)) continue;
      const group = conflictGroupOf(a, b);
      const entry = byGroup.get(group.code) ?? { label: group.label, codes: new Set<string>() };
      entry.codes.add(a).add(b);
      byGroup.set(group.code, entry);
    }
  });
  return [...byGroup.values()].map((entry) => {
    const groupCodes = [...entry.codes].sort(bySeedOrder);
    return {
      classLabel: entry.label,
      codes: groupCodes,
      labels: groupCodes.map((code) => ROWS_BY_CODE.get(code)?.label ?? code),
    };
  });
}

/** `selected` plus `added`, dropping whatever `added` contradicts. */
export function withoutConflictsFor(selected: readonly string[], added: string): string[] {
  return [
    ...selected.filter((code) => code !== added && !hazardCategoriesConflict(code, added)),
    added,
  ];
}
